package gr.tech.GRAttestation.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.GRAttestation.components.inputs.EmailInput
import gr.tech.GRAttestation.components.inputs.StringInput
import gr.tech.GRAttestation.components.inputs.ValidationTrigger
import gr.tech.GRAttestation.components.layouts.StoreUserPageLayout
import gr.tech.GRAttestation.components.widgets.loadingSpinner
import gr.tech.GRAttestation.infrastructure.api.checkStoreUserRegisterToken
import gr.tech.GRAttestation.infrastructure.api.finishStoreUserRegister
import gr.tech.GRAttestation.infrastructure.validations.*
import gr.tech.GRAttestation.models.storeusers.StoreUserFinishRegistrationRequest
import gr.tech.GRAttestation.navigation.SitePage
import gr.tech.GRAttestation.styles.*
import gr.tech.GRAttestation.styles.grstyles.Theme
import gr.tech.GRAttestation.styles.store.PortalSmallTextStyle
import gr.tech.GRAttestation.util.Constants
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.onSubmit
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

@Page(routeOverride = "finish-registration")
@Composable
fun FinishRegistrationPage() {
    StoreUserPageLayout(
        title = "Finish Registration",
        requiredRoles = emptyList()
    ) {
        FinishRegistrationScreen()
    }
}

@Composable
fun FinishRegistrationScreen() {
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()

    var storeName: String? by remember { mutableStateOf(null) }

    var registerToken: String? by remember { mutableStateOf(ctx.route.params["token"]) }
    var tokenValid: Boolean by remember { mutableStateOf(false) }
    var loading: Boolean by remember { mutableStateOf(true) }

    var userEmail by remember { mutableStateOf("") }
    var userConfirmEmail by remember { mutableStateOf("") }
    var userFirstName by remember { mutableStateOf("") }
    var userLastName by remember { mutableStateOf("") }
    var userPassword by remember { mutableStateOf("") }
    var userConfirmPassword by remember { mutableStateOf("") }

    var submitPressed by remember { mutableStateOf(false) }

    var emailError by remember { mutableStateOf("") }
    var confirmEmailError by remember { mutableStateOf("") }
    var firstNameError by remember { mutableStateOf("") }
    var lastNameError by remember { mutableStateOf("") }
    var passwordError by remember { mutableStateOf("") }
    var confirmPasswordError by remember { mutableStateOf("") }

    var errorText by remember { mutableStateOf("") }

    LaunchedEffect(Unit) {
        registerToken?.let { token ->
            val responseWithStoreName = checkStoreUserRegisterToken(token)
            if (responseWithStoreName.success && responseWithStoreName.data != null) {
                storeName = responseWithStoreName.data
                tokenValid = true
                errorText = ""
            } else {
                tokenValid = false
                errorText = responseWithStoreName.error.message
            }
            loading = false
        } ?: run {
            tokenValid = false
            loading = false
            errorText = "Invalid registration link. Please try again!"
        }
    }

    fun handleSubmit() {
        errorText = ""

        emailError = validateEmail(userEmail)
        confirmEmailError = validateConfirmEmail(userEmail, userConfirmEmail)
        firstNameError = validateName(userFirstName, "First name")
        lastNameError = validateName(userLastName, "Last name")
        passwordError = validatePassword(userPassword)
        confirmPasswordError = validateConfirmPassword(userPassword, userConfirmPassword)

        val allValid = emailError.isBlank() && confirmEmailError.isBlank() &&
                firstNameError.isBlank() && lastNameError.isBlank() &&
                passwordError.isBlank() && confirmPasswordError.isBlank()

        if (allValid && registerToken != null) {
            val request = StoreUserFinishRegistrationRequest(
                registerToken = registerToken!!,
                email = userEmail.trim().lowercase(),
                firstName = userFirstName.trim(),
                lastName = userLastName.trim(),
                password = userPassword
            )

            submitPressed = true
            scope.launch {
                val response = finishStoreUserRegister(request)
                if (response.success) {
                    ctx.router.tryRoutingTo(SitePage.Login.route)
                } else {
                    errorText = response.error.message
                    submitPressed = false
                }
            }
        }
    }

    Box(
        modifier = AuthBoxStyle.toModifier(),
        contentAlignment = Alignment.Center
    ) {
        Column(modifier = AuthCardStyle.toModifier()) {

            // Card Header
            Column(
                modifier = AuthCardHeaderStyle.toModifier(),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Image(
                    src = "/grlogocenter.png",
                    alt = "Green Rebates Logo",
                    modifier = AuthLogoStyle.toModifier()
                )

                H2(attrs = AuthTitleStyle.toModifier().toAttrs()) {
                    Text("Finish Registration")
                }

                P(attrs = AuthSubtitleStyle.toModifier().toAttrs()) {
                    Text("ETO Grow Light Fixture Program 2026")
                }
            }

            // Card Body
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(24.px, 26.px),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                when {
                    loading -> {
                        loadingSpinner(modifier = Modifier.color(Theme.DarkGreen.rgb))
                    }

                    tokenValid -> {
                        Form(
                            attrs = Modifier
                                .fillMaxWidth()
                                .toAttrs {
                                    onSubmit {
                                        it.preventDefault()
                                        handleSubmit()
                                    }
                                }
                        ) {
                            storeName?.let {
                                H2(attrs = AuthTitleStyle.toModifier().classNames("mb-4", "fs-4").toAttrs()) {
                                    Text(it)
                                }
                            }

                            EmailInput(
                                direction = FlexDirection.Column,
                                inputId = "UserEmailInput",
                                labelText = "Email",
                                value = userEmail,
                                hasError = emailError.isNotBlank(),
                                showError = emailError.isNotBlank(),
                                placeholder = "store@email.com",
                                labelError = emailError,
                                labelModifier = AuthLabelStyle.toModifier(),
                                inputModifier = AuthInputStyle.toModifier().classNames("mb-2"),
                                minLength = Constants.VALIDATIONS.EMAIL_MIN_LENGTH,
                                maxLength = Constants.VALIDATIONS.EMAIL_MAX_LENGTH,
                                validationTrigger = if (emailError.isNotBlank()) ValidationTrigger.OnChange else ValidationTrigger.OnBlur,
                                onValueChange = { value ->
                                    userEmail = value
                                    if (emailError.isNotBlank()) emailError = ""
                                    if (confirmEmailError.isNotBlank()) confirmEmailError = ""
                                }
                            )

                            EmailInput(
                                direction = FlexDirection.Column,
                                inputId = "UserConfirmEmailInput",
                                labelText = "Confirm Email",
                                value = userConfirmEmail,
                                hasError = confirmEmailError.isNotBlank(),
                                showError = confirmEmailError.isNotBlank(),
                                placeholder = "store@email.com",
                                labelError = confirmEmailError,
                                labelModifier = AuthLabelStyle.toModifier(),
                                inputModifier = AuthInputStyle.toModifier().classNames("mb-2"),
                                minLength = Constants.VALIDATIONS.EMAIL_MIN_LENGTH,
                                maxLength = Constants.VALIDATIONS.EMAIL_MAX_LENGTH,
                                validationTrigger = if (confirmEmailError.isNotBlank()) ValidationTrigger.OnChange else ValidationTrigger.OnBlur,
                                onValueChange = { value ->
                                    userConfirmEmail = value
                                    if (confirmEmailError.isNotBlank()) confirmEmailError = ""
                                }
                            )

                            StringInput(
                                direction = FlexDirection.Column,
                                inputId = "UserFirstNameInput",
                                labelText = "First Name",
                                value = userFirstName,
                                hasError = firstNameError.isNotBlank(),
                                showError = firstNameError.isNotBlank(),
                                placeholder = "Mike",
                                labelError = firstNameError,
                                labelModifier = AuthLabelStyle.toModifier(),
                                inputModifier = AuthInputStyle.toModifier().classNames("mb-2"),
                                minLength = Constants.VALIDATIONS.NAME_MIN_LENGTH,
                                maxLength = Constants.VALIDATIONS.NAME_MAX_LENGTH,
                                validationTrigger = if (firstNameError.isNotBlank()) ValidationTrigger.OnChange else ValidationTrigger.OnBlur,
                                onValueChange = { value ->
                                    userFirstName = value
                                    if (firstNameError.isNotBlank()) firstNameError = ""
                                }
                            )

                            StringInput(
                                direction = FlexDirection.Column,
                                inputId = "UserLastNameInput",
                                labelText = "Last Name",
                                value = userLastName,
                                hasError = lastNameError.isNotBlank(),
                                showError = lastNameError.isNotBlank(),
                                placeholder = "Briskel",
                                labelError = lastNameError,
                                labelModifier = AuthLabelStyle.toModifier(),
                                inputModifier = AuthInputStyle.toModifier().classNames("mb-2"),
                                minLength = Constants.VALIDATIONS.NAME_MIN_LENGTH,
                                maxLength = Constants.VALIDATIONS.NAME_MAX_LENGTH,
                                validationTrigger = if (lastNameError.isNotBlank()) ValidationTrigger.OnChange else ValidationTrigger.OnBlur,
                                onValueChange = { value ->
                                    userLastName = value
                                    if (lastNameError.isNotBlank()) lastNameError = ""
                                }
                            )

                            StringInput(
                                direction = FlexDirection.Column,
                                inputId = "PasswordInput",
                                labelText = "Password",
                                value = userPassword,
                                hasError = passwordError.isNotBlank(),
                                showError = passwordError.isNotBlank(),
                                placeholder = "",
                                labelError = passwordError,
                                labelModifier = AuthLabelStyle.toModifier(),
                                inputModifier = AuthInputStyle.toModifier().classNames("mb-2"),
                                minLength = Constants.VALIDATIONS.PASSWORD_MIN_LENGTH,
                                maxLength = Constants.VALIDATIONS.PASSWORD_MAX_LENGTH,
                                password = true,
                                validationTrigger = if (passwordError.isNotBlank()) ValidationTrigger.OnChange else ValidationTrigger.OnBlur,
                                onValueChange = { value ->
                                    userPassword = value
                                    if (passwordError.isNotBlank()) passwordError = ""
                                    if (confirmPasswordError.isNotBlank()) confirmPasswordError = ""
                                }
                            )

                            StringInput(
                                direction = FlexDirection.Column,
                                inputId = "ConfirmPasswordInput",
                                labelText = "Confirm Password",
                                value = userConfirmPassword,
                                hasError = confirmPasswordError.isNotBlank(),
                                showError = confirmPasswordError.isNotBlank(),
                                placeholder = "",
                                labelError = confirmPasswordError,
                                labelModifier = AuthLabelStyle.toModifier(),
                                inputModifier = AuthInputStyle.toModifier(),
                                minLength = Constants.VALIDATIONS.PASSWORD_MIN_LENGTH,
                                maxLength = Constants.VALIDATIONS.PASSWORD_MAX_LENGTH,
                                password = true,
                                validationTrigger = if (confirmPasswordError.isNotBlank()) ValidationTrigger.OnChange else ValidationTrigger.OnBlur,
                                onValueChange = { value ->
                                    userConfirmPassword = value
                                    if (confirmPasswordError.isNotBlank()) confirmPasswordError = ""
                                }
                            )

                            P(attrs = PortalSmallTextStyle.toModifier().toAttrs()) {
                                Text("By completing registration, you agree to our ")
                                Link(
                                    path = SitePage.PrivacyPolicy.route,
                                    text = "Privacy Policy",
                                    modifier = Modifier.color(Theme.DarkGreen.rgb)
                                )
                                Text(" and ")
                                Link(
                                    path = SitePage.TermsConditions.route,
                                    text = "Terms of Service",
                                    modifier = Modifier.color(Theme.DarkGreen.rgb)
                                )
                                Text(".")
                            }

                            // Error Message
                            if (errorText.isNotEmpty()) {
                                P(attrs = AuthErrorStyle.toModifier().classNames("my-2").toAttrs()) {
                                    Text(errorText)
                                }
                            }

                            // Sign In Button
                            Button(
                                colorPalette = null,
                                enabled = !submitPressed,
                                modifier = AuthButtonStyle.toModifier()
                                    .classNames("mt-4", "w-100")
                                    .padding(12.px),
                                type = ButtonType.Submit,
                                onClick = { }
                            ) {
                                if (submitPressed) {
                                    loadingSpinner(modifier = Modifier.color(Colors.White))
                                } else {
                                    SpanText(
                                        text = "Finish",
                                        modifier = Modifier.fontSize(14.px).fontWeight(600)
                                    )
                                }
                            }
                        }
                    }

                    else -> {
                        P(attrs = AuthErrorStyle.toModifier().margin(bottom = 20.px).toAttrs()) {
                            Text(errorText)
                        }

                        Link(
                            text = "Back to Login",
                            path = SitePage.Login.route,
                            modifier = Modifier
                                .fontSize(0.75.cssRem)
                                .color(Theme.DarkGreen.rgb)
                        )
                    }
                }
            }
        }
    }
}

